import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import set from 'lodash/set';
import sortBy from 'lodash/sortBy';
import { Box, Text, Flex, xcss } from '@atlaskit/primitives';
import { abgGraphqlClient, ProxyGraphClients } from '@atlassian/temp-client-utils';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { EntitlementIcon } from '@css-xp/base';
import { generatedTypesNew } from '@css-xp/graphtypes';
import { getRouteUrl } from '@css-xp/ppc-utils';
import { routePaths } from '@css-xp/ppc-constants';
import { fetchEntitlementListForIncludedProducts } from '../queries/fetch-entitlement-for-included-products';
import { EntitlementIconStyle, TitleStyle } from './included-products-sidebar.styles';

type ProductData = generatedTypesNew.Maybe<
  Pick<
    generatedTypesNew.TransactionAccountProduct,
    'id' | 'entitlementNumber' | 'productId' | 'productName' | 'cloudSiteHostname' | 'logoId'
  >
>;

interface ProductListData {
  [key: string]: ProductData;
}

interface ProductListProps {
  transactionAccountId: string;
  ids: string[];
  title: string;
  productsData: ProductListData;
  version?: number;
}

const ProductList = ({ transactionAccountId, ids, title, productsData, version }: ProductListProps) => {
  const productListData: ProductData[] = [];
  ids.forEach((id) => {
    if (productsData[id]) {
      productListData.push(productsData[id]);
    }
  });

  if (productListData.length === 0) {
    return null;
  }

  const subscriptionsRoutePPC = getRouteUrl(routePaths.subscriptions, {
    txaId: transactionAccountId,
  });

  return (
    <Box paddingBlockStart="space.400">
      <TitleStyle>
        <Text weight="bold" size="medium">
          {title} ({productListData.length})
        </Text>
      </TitleStyle>
      <>
        {sortBy(productListData, (productData) => productData?.productName).map((productDetail) => {
          return (
            <Box paddingBlockStart="space.150" key={productDetail?.id}>
              <Flex>
                <Box xcss={xcss({ width: version === 2 ? '10%' : '8%' })} padding="space.050">
                  <EntitlementIconStyle>
                    <EntitlementIcon
                      productId={productDetail?.productId ?? ''}
                      logoId={productDetail?.logoId}
                      isCloud
                    />
                  </EntitlementIconStyle>
                </Box>
                <Box paddingInlineStart="space.100">
                  <Flex direction="column">
                    <Link to={`${subscriptionsRoutePPC}${productDetail?.id}`} data-testid="parent-product-link">
                      {productDetail?.productName}
                    </Link>
                    <Text color="color.text.subtlest" size="small">
                      {productDetail?.cloudSiteHostname}
                    </Text>
                    <Text color="color.text.subtlest" size="small">
                      {productDetail?.entitlementNumber}
                    </Text>
                  </Flex>
                </Box>
              </Flex>
            </Box>
          );
        })}
      </>
    </Box>
  );
};

interface IncludedProductsSidebarProps {
  transactionAccountId: string;
  selectedEntitlementDetails: {
    id?: string;
    productName?: string;
    entitlementNumber?: string;
  };
  linkedAppsId: string[];
  linkedAddOnsId: string[];
  linkedCollectionProductsId: string[];
  linkedEnterpriseInstancesId: string[];
  linkedSandboxId: string[];
  version?: number;
  isAppificationEnabled?: boolean;
}

export const IncludedProductsSidebar = ({
  transactionAccountId,
  selectedEntitlementDetails,
  linkedAppsId,
  linkedAddOnsId,
  linkedCollectionProductsId,
  linkedEnterpriseInstancesId,
  linkedSandboxId,
  version,
  isAppificationEnabled,
}: IncludedProductsSidebarProps) => {
  const [productsInfo, setProductsInfo] = useState<{
    data: ProductListData;
    loading: boolean;
    error: null | Error;
  }>({
    data: {},
    loading: true,
    error: null,
  });

  const ansClient = useMemo(() => abgGraphqlClient(ProxyGraphClients.ANS), []);
  const fetchProductDetails = useCallback(async () => {
    try {
      setProductsInfo({ data: {}, loading: true, error: null });
      const fetchEntitlementIds = [
        ...linkedAppsId,
        ...linkedAddOnsId,
        ...linkedCollectionProductsId,
        ...linkedEnterpriseInstancesId,
        ...linkedSandboxId,
      ];
      const { data }: { data: generatedTypesNew.FetchEntitlementListForIncludedProductsQuery } = await ansClient.query({
        query: fetchEntitlementListForIncludedProducts,
        variables: {
          id: transactionAccountId,
          filterOptions: [
            {
              key: 'or',
              value: JSON.stringify(
                fetchEntitlementIds.map((entitlementId) => {
                  return { id: entitlementId };
                })
              ),
            },
          ],
          searchOptions: {
            first: fetchEntitlementIds.length,
            offset: 0,
          },
        },
      });

      const productsData: ProductListData = {};
      data?.transactionAccount?.productSubscriptionsInPPC?.nodes?.forEach((product) => {
        if (product?.id) {
          set(productsData, product.id, product);
        }
      });
      setProductsInfo({ data: productsData, loading: false, error: null });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      setProductsInfo({ data: {}, loading: false, error: err as any });
    }
  }, [
    ansClient,
    linkedAddOnsId,
    linkedAppsId,
    linkedCollectionProductsId,
    linkedEnterpriseInstancesId,
    linkedSandboxId,
    transactionAccountId,
  ]);

  useEffect(() => {
    fetchProductDetails();
  }, [fetchProductDetails]);

  const subscriptionsRoutePPC = getRouteUrl(routePaths.subscriptions, {
    txaId: transactionAccountId,
  });

  const displayTitle = useMemo(() => {
    if (linkedCollectionProductsId.length > 0 || linkedEnterpriseInstancesId.length > 0) {
      return 'Products included under your';
    }
    return 'Installed items for';
  }, [linkedCollectionProductsId, linkedEnterpriseInstancesId]);

  return (
    <Box paddingBlockStart={version === 2 ? 'space.100' : 'space.0'}>
      <Text weight="bold" size="large">
        Linked subscriptions
      </Text>
      {productsInfo.loading ? (
        <LoadingIndicator />
      ) : (
        <>
          <Box paddingBlockStart="space.400">
            <Text color="color.text.subtlest" size="medium">
              {displayTitle}{' '}
              <Link to={`${subscriptionsRoutePPC}${selectedEntitlementDetails?.id}`} data-testid="parent-product-link">
                {selectedEntitlementDetails?.productName}
              </Link>{' '}
              ({selectedEntitlementDetails?.entitlementNumber || ''})
            </Text>
          </Box>
          <ProductList
            transactionAccountId={transactionAccountId}
            ids={linkedCollectionProductsId}
            title={isAppificationEnabled ? 'Atlassian apps' : 'Products'}
            productsData={productsInfo.data}
            version={version}
          />
          <ProductList
            transactionAccountId={transactionAccountId}
            ids={linkedAddOnsId}
            title="Add-ons"
            productsData={productsInfo.data}
            version={version}
          />
          <ProductList
            transactionAccountId={transactionAccountId}
            ids={linkedAppsId}
            title={isAppificationEnabled ? 'Marketplace apps' : 'Apps'}
            productsData={productsInfo.data}
            version={version}
          />
          <ProductList
            transactionAccountId={transactionAccountId}
            ids={linkedEnterpriseInstancesId}
            title="Included products"
            productsData={productsInfo.data}
            version={version}
          />
          <ProductList
            transactionAccountId={transactionAccountId}
            ids={linkedSandboxId}
            title="Sandbox"
            productsData={productsInfo.data}
            version={version}
          />
        </>
      )}
    </Box>
  );
};
